import type { CommercialStatus, KanbanStatusOp, Product, ProductRow, ReplenishmentPriority, Sale } from '../types'
import { predictMonthlyDemand } from './forecast'

const MS_PER_DAY = 1000 * 60 * 60 * 24
// Espelha BX.xlsm aba "Config." célula B3
const WORKING_DAYS_PER_MONTH = 26
// Espelha BX.xlsm aba "Config." célula B2 (Estoque de Segurança fixo em unidades).
// Mantido em 0: o colchão agora é em DIAS por curva ABC (SafetyDaysConfig), que
// escala com a demanda diária do item (ver computeReplenishment).
const SAFETY_STOCK = 0

/**
 * Anti-ruptura: dias de cobertura de segurança por curva ABC, somados ao lead time
 * no ponto de ressuprimento. Vem das colunas companies.safety_days_a/b/c.
 * A (mais crítica) costuma ter mais dias que C.
 */
export interface SafetyDaysConfig {
  a: number
  b: number
  c: number
}

/** Config neutra: sem colchão (comportamento anterior a Fase 1). */
export const DEFAULT_SAFETY_DAYS: SafetyDaysConfig = { a: 0, b: 0, c: 0 }

/**
 * Resolve os dias de segurança aplicáveis ao item pela sua curva ABC.
 * Sem curva definida (abc null) → tier C (mais conservador no caixa), coerente com
 * a migration 20260613_company_safety_stock.sql.
 */
export function resolveSafetyDays(abc: 'A' | 'B' | 'C' | null | undefined, cfg: SafetyDaysConfig): number {
  if (abc === 'A') return cfg.a
  if (abc === 'B') return cfg.b
  return cfg.c
}

// Janela: últimos `months` meses calendário COMPLETOS (não corridos). Ex.: em jun/2026,
// pega vendas de 2026-02-01 (inclusive) a 2026-06-01 (exclusive) — fev, mar, abr, mai.
// Diferente da janela corrida anterior (`agora − months*30d`), que misturava parte do
// mês atual e gerava ruído em fechamentos. Divisor permanece `months` (não nº de dias).
export function computeMonthlyAvgFromSales(sales: Sale[], months = 4): number {
  if (sales.length === 0) return 0
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth() - months, 1)
  const end = new Date(now.getFullYear(), now.getMonth(), 1)
  let total = 0
  for (const s of sales) {
    const d = new Date(s.sale_date)
    if (d >= start && d < end) total += Number(s.quantity)
  }
  return total / months
}

/**
 * Série de quantidades por mês calendário COMPLETO (mais antigo → mais recente),
 * dos últimos `months` meses, excluindo o mês corrente — mesma janela de
 * computeMonthlyAvgFromSales. Alimenta a previsão de demanda (tendência) na Fase 2.
 */
export function monthlySalesSeries(sales: Sale[], months = 6): number[] {
  const now = new Date()
  const buckets = new Array<number>(months).fill(0)
  for (const s of sales) {
    const d = new Date(s.sale_date)
    // monthsBack: 1 = mês anterior ao corrente; months = mês mais antigo da janela.
    const monthsBack = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
    if (monthsBack >= 1 && monthsBack <= months) {
      buckets[months - monthsBack] += Number(s.quantity) || 0 // idx 0 = mais antigo
    }
  }
  return buckets
}

export function lastSaleDate(sales: Sale[]): string | null {
  if (sales.length === 0) return null
  return sales.reduce<string | null>((latest, s) => {
    if (!latest || new Date(s.sale_date) > new Date(latest)) return s.sale_date
    return latest
  }, null)
}

export function daysSince(date: string | null): number | null {
  if (!date) return null
  return Math.floor((Date.now() - new Date(date).getTime()) / MS_PER_DAY)
}

/**
 * Espelha a fórmula da coluna R (Prioridade de Compra) da aba Kanban Matriz:
 *   IF(sem fornecedor/lead time) → "SemFornecedor"  (cadastro pendente — sem como decidir)
 *   IF(F=0, D>0)         → "Alto"     (sem histórico de venda mas com estoque)
 *   IF(I <= 0)           → "Zerado"   (sem cobertura)
 *   IF(I < E)            → "Baixo"
 *   IF(I < E × 1.5)      → "Comprar"
 *   IF(I < E × 2)        → "Médio"
 *   ELSE                 → "Alto"
 * onde D=estoque, F=média mensal, I=dias de cobertura, E=lead time
 */
export function getPriority(row: {
  current_stock: number
  monthly_avg: number
  coverage_days: number
  lead_time: number | null
}): ReplenishmentPriority {
  // Sem fornecedor/lead time cadastrado: não há como calcular ponto de ressuprimento.
  // Sinaliza para o usuário ir cadastrar (cor mostarda no Painel BX).
  if (row.lead_time === null || row.lead_time === undefined) return 'SemFornecedor'
  // Estoque abaixo de 0,0001 é considerado zerado operacionalmente → sempre Zerado (roxo)
  if (row.current_stock < 0.0001) return 'Zerado'
  if (row.monthly_avg === 0) return 'Alto'
  if (row.coverage_days <= 0) return 'Zerado'
  if (row.coverage_days < row.lead_time)         return 'Baixo'
  if (row.coverage_days < row.lead_time * 1.5)   return 'Comprar'
  if (row.coverage_days < row.lead_time * 2)     return 'Médio'
  return 'Alto'
}

// Status Kanban que "ocultam" um item zerado das contagens de ruptura:
// o saldo é 0, mas há um motivo operacional conhecido (não é ruptura acionável).
export const HIDES_ZERADO: Set<KanbanStatusOp> = new Set([
  'Ruptura no Fornecedor', 'Consignado', 'Bloqueado', 'Standby',
])

/**
 * Régua única de "ruptura acionável": saldo zerado E sem motivo operacional
 * que justifique a ausência de ação de compra. Usada no Painel BX e no
 * Dashboard de Estoque para que os indicadores fiquem coerentes.
 */
export function isRupturaAcionavel(currentStock: number, kanbanStatus: KanbanStatusOp): boolean {
  return currentStock <= 0 && !HIDES_ZERADO.has(kanbanStatus)
}

/** Multiplicador anti-ruptura: item em ruptura compra o DOBRO. */
export const RUPTURA_QTY_MULTIPLIER = 2

/**
 * Anti-ruptura: dobra a quantidade do pedido para itens EM RUPTURA. Comprar a
 * sugestão "normal" de um item zerado o faz re-romper dentro do lead time (sem
 * folga para disparar a próxima compra a tempo); dobrar cria essa folga. Só
 * ruptura acionável dobra (Baixo/Comprar/Médio/Alto inalterados); qty 0 fica 0.
 */
export function aplicarDobraRuptura(qty: number, currentStock: number, kanbanStatus: KanbanStatusOp): number {
  return qty > 0 && isRupturaAcionavel(currentStock, kanbanStatus) ? qty * RUPTURA_QTY_MULTIPLIER : qty
}

/**
 * Sinal preventivo anti-ruptura (tarefa principal do Miguel: não deixar zerar).
 * A cobertura em dias já entrou na zona em que o item pode zerar antes da reposição
 * (lead time + colchão de segurança da curva) e NÃO há pedido em aberto que o cubra.
 *
 * Independe da cor do semáforo (getPriority continua espelhando a BX.xlsm) — serve
 * para avisar o comprador ANTES de o item virar roxo. Sem demanda (daily_avg = 0)
 * o item não rompe; sem fornecedor (lead_time null) ele aparece como "SemFornecedor"
 * (cadastro pendente), não como risco de ruptura.
 */
export function isAtRuptureRisk(input: {
  coverage_days: number
  lead_time: number | null
  safety_days: number
  daily_avg: number
  /** Há pedido em aberto (Pendente/Enviado) que repõe o item. Fase 1: qualquer
   *  pedido em aberto conta como cobertura (mesma régua do abate de open_qty na
   *  sugestão); a aderência por data de chegada fica para a Fase 2. */
  has_covering_order: boolean
}): boolean {
  const { coverage_days, lead_time, safety_days, daily_avg, has_covering_order } = input
  if (daily_avg <= 0) return false
  if (lead_time === null) return false
  if (has_covering_order) return false
  return coverage_days <= lead_time + safety_days
}

/**
 * Núcleo da reposição: a partir de estoque + média mensal + lead time, deriva
 * todos os campos calculados (espelha as colunas G–P, R do BX.xlsm).
 * Fonte única usada tanto pelo Painel BX da matriz (via buildProductRow) quanto
 * pela consolidação das filiais (useFilialNeeds), evitando divergência de fórmula.
 */
export function computeReplenishment(input: {
  current_stock: number
  monthly_avg: number
  /** Lead time em dias do fornecedor; `null` quando o produto está sem fornecedor cadastrado. */
  lead_time: number | null
  /** Quantidade já pedida em aberto (Pendente/Enviado). Abate só na sugestão. */
  open_qty?: number
  /** Anti-ruptura: dias de cobertura de segurança (da curva ABC) somados ao lead time
   *  no ponto de ressuprimento. Default 0 = comportamento anterior. */
  safety_days?: number
  /** Fase 2: demanda mensal usada SÓ para a compra (ponto de ressuprimento, gap,
   *  sugestão e cobertura de risco). Default = monthly_avg (sem previsão). A cobertura
   *  e a prioridade do semáforo continuam pela média plana (monthly_avg). */
  demand_for_reorder?: number
}): {
  daily_avg: number
  coverage_months: number
  coverage_days: number
  /** Cobertura em dias na demanda prevista (base do em_risco; NÃO do semáforo). */
  reorder_coverage_days: number
  reorder_point: number
  gap_to_buy: number
  purchase_suggestion: number
  priority: ReplenishmentPriority
} {
  const { current_stock, monthly_avg, lead_time, open_qty = 0, safety_days = 0, demand_for_reorder } = input
  // BX.xlsm coluna G: =IF(F2=0, 0, F2/Config!B3)
  const daily_avg = monthly_avg > 0 ? monthly_avg / WORKING_DAYS_PER_MONTH : 0
  // BX.xlsm coluna H: =IF(F2=0, 0, D2/F2)
  const coverage_months = monthly_avg > 0 ? current_stock / monthly_avg : 0
  // BX.xlsm coluna I: =IF(G2=0, 0, D2/G2) — cobertura usa só o saldo (não abate pedido)
  const coverage_days = daily_avg > 0 ? current_stock / daily_avg : 0
  // Demanda para a COMPRA (Fase 2): por padrão = média plana (sem previsão). Quando a
  // demanda acelera, demand_for_reorder > monthly_avg → ponto de ressuprimento e sugestão
  // sobem, antecipando a ruptura. NÃO altera coverage_days/priority (semáforo do Painel BX).
  const reorder_demand = demand_for_reorder ?? monthly_avg
  const reorder_daily = reorder_demand > 0 ? reorder_demand / WORKING_DAYS_PER_MONTH : 0
  const reorder_coverage_days = reorder_daily > 0 ? current_stock / reorder_daily : coverage_days
  // Sem lead time não dá para calcular ponto de ressuprimento nem sugestão.
  // Anti-ruptura: o ponto de ressuprimento cobre lead time + colchão de segurança
  // (em dias da curva), de modo que cada compra restaura a margem contra atraso/pico.
  const reorder_point = lead_time === null ? 0 : (lead_time + safety_days) * reorder_daily + SAFETY_STOCK
  // Gap abate o saldo projetado (estoque + pedido em aberto) para não comprar duplicado.
  const gap_to_buy = lead_time === null ? 0 : Math.max(0, reorder_point - current_stock - open_qty)
  // BX.xlsm coluna P: =ROUNDUP(K2, 0)
  const purchase_suggestion = Math.ceil(gap_to_buy)

  const priority = getPriority({ current_stock, monthly_avg, coverage_days, lead_time })

  return { daily_avg, coverage_months, coverage_days, reorder_coverage_days, reorder_point, gap_to_buy, purchase_suggestion, priority }
}

// ── Motor de Sugestão de Compras: score comercial ───────────────────────────

/** Pesos do score comercial (espelham a proposta; ajustáveis aqui). */
export const SCORE_WEIGHTS = {
  abcA: 30, abcB: 20, abcC: 10,
  // Cobertura (dias) vs lead time — quanto menor a cobertura, mais urgente comprar.
  // Espelha as faixas de prioridade do Painel BX (getPriority).
  coberturaZero: 30,    // cobertura <= 0 (com demanda)
  coberturaBaixa: 24,   // < lead time
  coberturaComprar: 16, // < lead time × 1.5
  coberturaMedia: 8,    // < lead time × 2
  margemAlta: 20, margemAltaMin: 40, // margem_pct >= 40
  vendaCrescente: 15,
  rupturaRecente: 20,
  pedidoAberto: -40,
} as const

/**
 * Pontuação graduada pela cobertura em dias (relativa ao lead time), espelhando as
 * faixas de getPriority. Sem demanda (daily_avg = 0) → 0 pontos: não há urgência de
 * comprar item que não gira, mesmo com saldo baixo.
 */
function coberturaScore(coverage_days: number, lead_time: number | null, daily_avg: number): number {
  const w = SCORE_WEIGHTS
  if (daily_avg <= 0) return 0
  // Sem lead time não dá para graduar a faixa — score zero (ranking do motor comercial
  // ignora o item; ele aparece no Painel BX como "SemFornecedor" para cadastro).
  if (lead_time === null) return 0
  if (coverage_days <= 0) return w.coberturaZero
  if (coverage_days < lead_time) return w.coberturaBaixa
  if (coverage_days < lead_time * 1.5) return w.coberturaComprar
  if (coverage_days < lead_time * 2) return w.coberturaMedia
  return 0
}

/**
 * Score comercial (0–100). Reusa sinais já disponíveis no Painel BX e serve só para
 * ORDENAR a lista — a classificação de status (chips) segue a régua do Painel BX
 * (ver commercialStatusFromPriority). A urgência por estoque é graduada pela cobertura
 * em dias (coberturaScore); "ruptura recente" usa a régua de ruptura acionável ao vivo
 * (aproximação — não há log de stockout); "venda crescente" vem da velocidade
 * (30d vs 30d anteriores).
 */
export function computeCommercialScore(input: {
  abc: 'A' | 'B' | 'C' | null
  current_stock: number
  kanban_status: KanbanStatusOp
  margin_pct: number | null
  venda_crescente: boolean
  open_qty: number
  coverage_days: number
  lead_time: number | null
  daily_avg: number
}): { score: number } {
  const w = SCORE_WEIGHTS
  let score = 0
  score += input.abc === 'A' ? w.abcA : input.abc === 'B' ? w.abcB : w.abcC
  score += coberturaScore(input.coverage_days, input.lead_time, input.daily_avg)
  if ((input.margin_pct ?? 0) >= w.margemAltaMin) score += w.margemAlta
  if (input.venda_crescente) score += w.vendaCrescente
  if (isRupturaAcionavel(input.current_stock, input.kanban_status)) score += w.rupturaRecente
  if (input.open_qty > 0) score += w.pedidoAberto

  score = Math.max(0, Math.min(100, score))
  return { score }
}

/**
 * Status comercial (chips) seguindo EXATAMENTE a régua de prioridade do Painel BX
 * (getPriority, baseada em cobertura vs lead time). Mapeamento das 5 faixas do BX
 * para os 4 status: Zerado → Compra urgente; Baixo/Comprar → Comprar;
 * Médio → Monitorar; Alto → Não comprar.
 */
export function commercialStatusFromPriority(priority: ReplenishmentPriority): CommercialStatus {
  switch (priority) {
    case 'SemFornecedor': return 'Não comprar'
    case 'Zerado':  return 'Compra urgente'
    case 'Baixo':   return 'Comprar'
    case 'Comprar': return 'Comprar'
    case 'Médio':   return 'Monitorar'
    case 'Alto':    return 'Não comprar'
  }
}

type ProductRowBase = Omit<
  ProductRow,
  'active_order_number' | 'active_order_date' | 'active_order_status' | 'filial_suggestion' | 'total_suggestion'
  | 'open_qty' | 'estoque_max' | 'commercial_score' | 'commercial_status' | 'em_risco'
>

export function buildProductRow(product: Product, sales: Sale[], open_qty = 0, safetyDays = 0): ProductRowBase {
  const productSales = sales.filter(s => s.product_id === product.id)
  // Última venda: prioriza dado de sales; se vazio, usa last_movement_at vindo do Tiny
  const lastSale = lastSaleDate(productSales) ?? product.last_movement_at ?? null

  const monthly_avg_calculated = computeMonthlyAvgFromSales(productSales)
  const monthly_avg = product.monthly_avg_manual ?? monthly_avg_calculated

  // Fase 2: demanda mensal prevista (tendência, viés nunca-reduzir) para a COMPRA.
  // Override manual de média manda — não previsão por cima de número fixado pelo usuário.
  const demanda_prevista = product.monthly_avg_manual != null
    ? monthly_avg
    : predictMonthlyDemand(monthlySalesSeries(productSales), monthly_avg_calculated)

  // Sem fornecedor cadastrado ou sem lead_time_days → null. computeReplenishment
  // zera ponto de ressuprimento/gap/sugestão e devolve priority='SemFornecedor'
  // para o usuário corrigir o cadastro do produto.
  const lead_time = product.suppliers?.lead_time_days ?? null
  const calc = computeReplenishment({
    current_stock: product.current_stock, monthly_avg, lead_time, open_qty,
    safety_days: safetyDays, demand_for_reorder: demanda_prevista,
  })

  return {
    ...product,
    monthly_avg,
    demanda_prevista,
    daily_avg: calc.daily_avg,
    coverage_days: calc.coverage_days,
    coverage_months: calc.coverage_months,
    reorder_coverage_days: calc.reorder_coverage_days,
    reorder_point: calc.reorder_point,
    gap_to_buy: calc.gap_to_buy,
    last_sale_at: lastSale,
    days_without_sale: daysSince(lastSale),
    purchase_suggestion: calc.purchase_suggestion,
    priority: calc.priority,
  }
}

// "SemFornecedor" entra primeiro para destacar no topo da lista (cadastro pendente);
// as demais seguem a régua de prioridade do BX.xlsm.
export const PRIORITY_ORDER: ReplenishmentPriority[] = ['SemFornecedor', 'Zerado', 'Baixo', 'Comprar', 'Médio', 'Alto']

// Cores extraídas da aba "Kanban Matriz" do BX.xlsm (formatação condicional)
// Aplicadas via style inline com !important pra garantir override do tailwind hover.
// "SemFornecedor" usa mostarda (#7F6000) — fora da paleta do semáforo do BX, distinta de
// qualquer status Kanban, sinalizando "cadastro pendente, não é decisão de compra".
export const PRIORITY_ROW: Record<ReplenishmentPriority, string> = {
  SemFornecedor: 'bg-[#7F6000] hover:brightness-110 text-white',
  Zerado:  'bg-[#7030A0] hover:brightness-110 text-white',
  Baixo:   'bg-[#FF0000] hover:brightness-110 text-white',
  Comprar: 'bg-[#00B0F0] hover:brightness-110 text-gray-900',
  Médio:   'bg-[#FFFF00] hover:brightness-95 text-gray-900',
  Alto:    'bg-[#00B050] hover:brightness-110 text-white',
}

export const PRIORITY_BADGE: Record<ReplenishmentPriority, string> = {
  SemFornecedor: 'bg-[#7F6000] text-white',
  Zerado:  'bg-[#7030A0] text-white',
  Baixo:   'bg-[#FF0000] text-white',
  Comprar: 'bg-[#00B0F0] text-gray-900',
  Médio:   'bg-[#FFFF00] text-gray-900',
  Alto:    'bg-[#00B050] text-white',
}

// Cores de chip/badge para status (sem hover, para chips de filtro)
export const STATUS_BADGE: Partial<Record<KanbanStatusOp, string>> = {
  'Compra Aprovada':       'bg-[#ED7D31] text-white',
  'Bloqueado':             'bg-[#BFBFBF] text-gray-900',
  'Consignado':            'bg-[#FFC000] text-gray-900',
  'Ruptura no Fornecedor': 'bg-[#F8CBAD] text-gray-900',
  'Standby':               'bg-[#5B7C99] text-white',
}

// Status Kanban que SOBRESCREVEM a cor da prioridade (regra do BX.xlsm)
// Quando o status for "Analisar", a cor da prioridade prevalece
export const STATUS_ROW_OVERRIDE: Partial<Record<KanbanStatusOp, string>> = {
  'Compra Aprovada':       'bg-[#ED7D31] hover:brightness-110 text-white',       // laranja
  'Inativar':              'bg-[#D9D9D9] hover:brightness-95 text-gray-900',     // cinza claro
  'Bloqueado':             'bg-[#BFBFBF] hover:brightness-95 text-gray-900',     // cinza claro
  'Consignado':            'bg-[#FFC000] hover:brightness-110 text-gray-900',    // dourado
  'Ruptura no Fornecedor': 'bg-[#F8CBAD] hover:brightness-95 text-gray-900',     // laranja claro
  'Standby':               'bg-[#5B7C99] hover:brightness-110 text-white',       // cinza-azulado
  'Em Entrega':            'bg-[#2E5C8A] hover:brightness-110 text-white',       // azul royal
  'Recebido':              'bg-[#385723] hover:brightness-110 text-white',       // verde-escuro
  'Priorizar':             'bg-[#C00000] hover:brightness-110 text-white',       // vermelho forte
}

/**
 * Aplica a fórmula do BX.xlsm aba Kanban Matriz, coluna [Quant. Compra Aprovada]:
 *   vazio  → "Analisar"
 *   "S"    → "Standby"
 *   "C"    → "Consignado"
 *   "RF"   → "Ruptura no Fornecedor"
 *   "B"    → "Bloqueado"
 *   n > 0  → "Compra Aprovada" (único input que gera pedido)
 *   else   → "Analisar"
 */
export function deriveKanbanFromInput(raw: string | null | undefined): {
  status: KanbanStatusOp
  numericQty: number
} {
  const v = (raw ?? '').trim().toUpperCase()
  if (v === '')   return { status: 'Analisar', numericQty: 0 }
  if (v === 'S')  return { status: 'Standby', numericQty: 0 }
  if (v === 'C')  return { status: 'Consignado', numericQty: 0 }
  if (v === 'RF') return { status: 'Ruptura no Fornecedor', numericQty: 0 }
  if (v === 'B')  return { status: 'Bloqueado', numericQty: 0 }
  const n = Number(v.replace(',', '.'))
  if (!Number.isNaN(n) && n > 0) return { status: 'Compra Aprovada', numericQty: Math.floor(n) }
  return { status: 'Analisar', numericQty: 0 }
}

/** Retorna as classes da linha — Status Kanban sobrescreve Prioridade */
export function getRowClasses(priority: ReplenishmentPriority, status: KanbanStatusOp): string {
  return STATUS_ROW_OVERRIDE[status] ?? PRIORITY_ROW[priority]
}

/**
 * Gauge proporcional da linha do Painel BX (substitui a cor sólida): mostra, da esquerda
 * (vazio) à direita (cheio), o quanto o estoque caminhou pelas faixas do semáforo, ESPELHANDO
 * getPriority (cobertura × lead time). Reusa os mesmos hex de PRIORITY_ROW — não recolore nada.
 *
 * Escala 0→3×LT (dá largura visível à faixa verde/Alto). Faixas (fração de 3×LT):
 *   vermelho [0–1/3] (Baixo, cob<LT) · azul [1/3–1/2] (Comprar, <1,5×LT) ·
 *   amarelo [1/2–2/3] (Médio, <2×LT) · verde [2/3–1] (Alto, ≥2×LT).
 * Por construção a faixa acesa mais à direita == a cor da Prioridade (consistente com o chip).
 *
 * Retorna `null` quando a linha NÃO deve usar gauge (mantém cor sólida via getRowClasses):
 * status manual com override (Compra Aprovada, Consignado, …), SemFornecedor e Zerado
 * (roxo cheio + texto branco, sem halo). Demais → o linear-gradient.
 */
export function buildGaugeBackground(input: {
  priority: ReplenishmentPriority
  coverage_days: number
  lead_time: number | null
  kanban_status: KanbanStatusOp
}): string | null {
  const { priority, coverage_days, lead_time, kanban_status } = input
  if (STATUS_ROW_OVERRIDE[kanban_status]) return null
  if (priority === 'SemFornecedor') return null
  // Zerado = linha roxa cheia: sólido de PRIORITY_ROW (texto branco, sem halo) via getRowClasses.
  if (priority === 'Zerado') return null
  if (lead_time === null || lead_time <= 0) return null

  const full = 3 * lead_time
  const fill = priority === 'Alto' ? 1 : Math.max(0, Math.min(1, coverage_days / full))

  const ZONES: { end: number; hex: string }[] = [
    { end: 1 / 3, hex: '#FF0000' }, // Baixo   — vermelho
    { end: 1 / 2, hex: '#00B0F0' }, // Comprar — azul
    { end: 2 / 3, hex: '#FFFF00' }, // Médio   — amarelo
    { end: 1,     hex: '#00B050' }, // Alto    — verde
  ]
  const pct = (x: number) => `${(x * 100).toFixed(2)}%`

  const stops: string[] = []
  let cursor = 0
  let lastHex = ZONES[0].hex // cor do status atual (último zone aceso)
  for (const z of ZONES) {
    const litEnd = Math.min(z.end, fill)
    if (litEnd > cursor) {
      stops.push(`${z.hex} ${pct(cursor)}`, `${z.hex} ${pct(litEnd)}`)
      cursor = litEnd
      lastHex = z.hex
    }
    if (cursor >= fill) break
  }
  // O restante usa a cor do STATUS atual (não a do tema): a linha fica preenchida pelo status
  // até o fim, sem "vazio" — que no tema escuro aparecia quase preto. "Sempre o último status".
  if (fill < 1) stops.push(`${lastHex} ${pct(fill)}`, `${lastHex} 100%`)

  return `linear-gradient(to right, ${stops.join(', ')})`
}

/**
 * Update parcial para marcar um produto como consignado (identidade + ciclo).
 * Usado pelo toggle "Consignado" na coluna Quantidade do Pedido.
 */
export function consignedStateUpdate() {
  return {
    is_consigned: true,
    approval_input: null,
    approved_qty: 0,
    kanban_status: 'Consignado' as KanbanStatusOp,
  }
}

/**
 * Update parcial para marcar um produto como comprado (identidade + reset do ciclo).
 * Usado pelo toggle "Comprado" na coluna Quantidade do Pedido.
 */
export function purchasedStateUpdate() {
  return {
    is_consigned: false,
    approval_input: null,
    approved_qty: 0,
    kanban_status: 'Analisar' as KanbanStatusOp,
  }
}
